/**
 * instantGreetingAvailability
 *
 * Whether a voice deployment's configuration CAN open its calls on a saved
 * recording of the greeting — independent of whether `greetingPlayback` is set
 * to `instant`. The settings UI reads it to disable the toggle with the reason;
 * the voice relay's greeting planner reads it to refuse a call. One function, so
 * the two cannot disagree about which configurations play a recording.
 *
 * A recording stands in for words the deployment WROTE, said by a provider that
 * can be told the line was already said. So a configuration is unavailable when:
 *
 *   - `no-greeting`          — no scripted greeting, or one that is blank once
 *                              normalised: there is nothing to have recorded
 *   - `templated-greeting`   — the greeting holds `{{`: it is filled per call, so a
 *                              recording would be one caller's greeting played to
 *                              every other
 *   - `provider-unsupported` — the provider's adapter cannot seed a relay-played
 *                              greeting as history. OpenAI Realtime, which is also
 *                              what a config naming no provider runs on
 *   - `native-mode`          — GPT-Live in native (speech-to-speech) mode: only its
 *                              delegated sessions are proven not to greet again
 *   - `unversioned-prompt`   — Gemini Live records its greeting off the first call's
 *                              own audio and keys it on the published prompt version
 *                              the call ran on; an agent with no published version
 *                              has nothing to key it on
 *
 * Checked in that order, so the reason given is the first one a user would have
 * to fix. What it does NOT decide is per call — an outbound call, a greeting
 * overridden for one call, a prompt replaced for one call — and the relay
 * refuses those on its own.
 */

import { VOICE_PROVIDER_WHEN_UNSET } from "./Twilio/VoiceProviders.schema";
import { type VoiceConfigSettings } from "./VoiceConfig.schema";

export const INSTANT_GREETING_UNAVAILABLE_REASONS = [
  "no-greeting",
  "templated-greeting",
  "provider-unsupported",
  "native-mode",
  "unversioned-prompt"
] as const;

export type InstantGreetingUnavailableReason =
  (typeof INSTANT_GREETING_UNAVAILABLE_REASONS)[number];

export type InstantGreetingAvailability =
  | { available: true; greetingText: string }
  | { available: false; reason: InstantGreetingUnavailableReason };

/**
 * The voice config fields the answer depends on. Named off `VoiceConfigSettings`,
 * so a TWILIO_VOICE deployment's root settings and an EMBED/API deployment's
 * `voiceSettings` both pass as they are stored — and a renamed field breaks here.
 */
export type InstantGreetingVoiceConfig = Pick<
  VoiceConfigSettings,
  "provider" | "gptLiveNativeMode" | "initialMessage"
>;

export interface InstantGreetingAvailabilityInput {
  voiceConfig: InstantGreetingVoiceConfig;
  /**
   * Whether the agent's calls run on a PUBLISHED prompt version. Read only for
   * Gemini Live. The settings UI answers it from the agent having a production
   * prompt version; the relay answers it from the version this call's prompt was
   * actually built from.
   */
  runsOnPublishedPrompt: boolean;
}

const TEMPLATE_OPENER = "{{";

/**
 * The greeting as it is compared and keyed: Unicode NFC, every run of whitespace
 * one space, trimmed. Case and punctuation are kept — both change delivery.
 */
export function normalizeGreetingText(text: string): string {
  return text.normalize("NFC").replace(/\s+/g, " ").trim();
}

export function instantGreetingAvailability(
  input: InstantGreetingAvailabilityInput
): InstantGreetingAvailability {
  const greetingText = normalizeGreetingText(input.voiceConfig.initialMessage ?? "");
  if (greetingText === "") return { available: false, reason: "no-greeting" };
  if (greetingText.includes(TEMPLATE_OPENER)) {
    return { available: false, reason: "templated-greeting" };
  }

  const provider = input.voiceConfig.provider ?? VOICE_PROVIDER_WHEN_UNSET;
  switch (provider) {
    case "openai":
      return { available: false, reason: "provider-unsupported" };
    case "gpt-live":
      // `?? false`, as the relay's session config resolves it: absent is delegated.
      if (input.voiceConfig.gptLiveNativeMode ?? false) {
        return { available: false, reason: "native-mode" };
      }
      return { available: true, greetingText };
    case "gemini":
      if (!input.runsOnPublishedPrompt) return { available: false, reason: "unversioned-prompt" };
      return { available: true, greetingText };
    default: {
      // A provider added to VOICE_PROVIDERS fails typecheck here until it is given an answer.
      const unhandled: never = provider;
      throw new Error(`Unhandled voice provider: ${String(unhandled)}`);
    }
  }
}
